import {store} from "../FolioStore.js";
import UsersAPI from '../../api/users/UserAPI.js';
import NotificationsAPI from '../../api/notification/NotificationsAPI.js';
import {PortfolioActions} from "./PortfolioActions.js";
import {event} from "vue-gtag";
import MixpanelService from "@/scripts/analytics/mixpanel.js";
import {logout} from "@/scripts/auth/keycloakAuth.js";
import {trackPendingSocialAuthCompletion} from "@/scripts/view/auth/helpers/authTracking.js";

function shouldBlockEditorFromUserSettings(settings) {
    if (settings?.currentSubscriptionPlan === 'VIP') {
        return false;
    }

    const subscription = settings?.currentSubscription;
    const targetTime = subscription?.nextBillingDate ? new Date(subscription.nextBillingDate).getTime() : null;

    return subscription?.status === 'past_due'
        || subscription?.status === 'canceled'
        || (targetTime ? Date.now() > targetTime : false);
}

let paymentPlanInfoPromise = null;

export const UserActions = {
    // create user
    createUser: async function () {
        try {
            let response = await UsersAPI.createMyUser();
            store.state.user = response.data.user;

            await this.getUserSettings();

            event('New user login', {
                'event_category': 'Project editor',
                'event_label': `User: ${store.state.user._id}`
            });
        } catch (error) {
            if (error.response && error.response.status === 409) { // User already exist
                window.location.reload();
            }
        }
    },

    // get user
    getUser: async function () {
        try {
            let response = await UsersAPI.getMyUser();
            await UserActions.setUserFromResponse(response.data);
            await trackPendingSocialAuthCompletion({
                user: response.data.user,
                newUserCreated: false,
            });
        } catch (error) {
            const status = error.response?.status;

            if (status === 400 || status === 404) {
                await UserActions.ensureUser();
                return;
            }

            //keycloak user also deleted
            if (status === 403) {
                return logout(window.location.origin);
            }

            throw error;
        }
    },
    getUserSeenNotifications: async function () {
        try {
            let response = await NotificationsAPI.getSeenSystemNotifications(store.state.user._id);
            store.state.user = {
                ...store.state.user,
                seenSystemNotifications: response.data
            };
        } catch (error) {
            console.log(error);
        }
    },

    async getUserSettings() {
        try {
            let response = await UsersAPI.getUserSettings(store.state.user._id);
            UserActions.setUserSettingsFromResponse(response.data);

            MixpanelService.identify(store.state.user._id)
            MixpanelService.setUser(store.state.user)
        } catch (error) {
            console.log('failed to get user settings', error)
        }
    },

    setUserSettingsFromResponse(settings) {
        const showTrialRestartedAlert =
            store.state.userSettings.showTrialRestartedAlert || !!settings.showTrialRestartedAlert;

        store.state.userSettings = {
            ...store.state.userSettings,
            ...settings,
            stripePrices: settings.stripePrices || store.state.userSettings.stripePrices,
            domainSubscriptions: settings.domainSubscriptions || store.state.userSettings.domainSubscriptions || [],
            invoices: settings.invoices || store.state.userSettings.invoices || [],
            showTrialRestartedAlert,
        };
        store.state.currentSubscriptionPlan = settings.currentSubscriptionPlan;
        UserActions.updateBlockedEditorState(store.state.userSettings);

        if (
            settings.currentSubscriptionPlan === 'VIP'
            && store.state.activeModal === 'SubscriptionExpiredModal'
        ) {
            store.commit('setActiveModal', null);
        }
    },

    updateBlockedEditorState(settings) {
        store.commit('setBlockedEditor', shouldBlockEditorFromUserSettings(settings));
    },

    hasPaymentPlanInfo() {
        const userSettings = store.state.userSettings;
        const monthlyPrice = userSettings?.stripePrices?.monthlyPrice;
        const yearlyPrice = userSettings?.stripePrices?.yearlyPrice;

        return !!userSettings?.currentSubscriptionPlan
            && !!monthlyPrice?.id
            && !!yearlyPrice?.id
            && !Number.isNaN(Number(monthlyPrice?.price))
            && !Number.isNaN(Number(yearlyPrice?.price));
    },

    async prefetchPaymentPlanInfo() {
        if (UserActions.hasPaymentPlanInfo() || !store.state.user?._id) {
            return;
        }

        if (!paymentPlanInfoPromise) {
            paymentPlanInfoPromise = UserActions.getUserSettings()
                .finally(() => {
                    paymentPlanInfoPromise = null;
                });
        }

        await paymentPlanInfoPromise;
    },

    async ensureUser(registrationSource = null) {
        let response = await UsersAPI.ensureMyUser(registrationSource);
        await UserActions.setUserFromResponse(response.data, {fetchSettingsFallback: false});
        await trackPendingSocialAuthCompletion({
            user: response.data.user,
            newUserCreated: response.data.newUserCreated,
        });
        return response.data;
    },

    async setUserFromResponse(data, {fetchSettingsFallback = true} = {}) {
        store.state.user = {
            ...data.user,
            paymentMethods: data.userPaymentMethods || [],
            isAdmin: data.isAdmin,
            isTemplateCreator: data.isTemplateCreator
        };
        store.state.emergencyModeSettings = data.emergencyModeSettings;
        if (data.user.checklistStatus) {
            store.commit("updateChecklistFinished", data.user.checklistStatus.finished);
        }
        if (data.userSettings) {
            UserActions.setUserSettingsFromResponse(data.userSettings);
        } else if (fetchSettingsFallback) {
            await UserActions.getUserSettings();
        }
        UserActions.setUserDataInSupportWidget(store.state.user.name, store.state.user.email);
    },

    deleteUserImage(imageUrl) {
        store.state.savingProject = true;
        if (imageUrl) {
            UsersAPI.deleteUserImage(store.state.user._id, imageUrl)
                .then(response => {
                })
                .catch(response => {
                })
                .finally(() => {
                    store.state.savingProject = false;
                })
        }
    },

    updateUserInDrip(dripEvent) {
        UsersAPI.updateUserInDrip(store.state.user._id, dripEvent)
            .then(response => {
            })
            .catch(error => {
                console.log(error)
            })
    },

    async refreshData() {
        store.commit("view/updateViewModeReadiness", false)
        store.state.userDataAndPortfolioDataLoaded = false;
        await UserActions.getUser();
        await Promise.all([
            PortfolioActions.getPortfolios(),
            PortfolioActions.getAllCaseStudyPages(),
            PortfolioActions.getLatestNotifications(),
            PortfolioActions.getAllAvalibleFonts(),
        ]);
        store.commit("view/updateViewModeReadiness", true)
    },

    async refreshEditorData() {
        store.commit("view/updateViewModeReadiness", false)
        store.state.userDataAndPortfolioDataLoaded = false;
        await UserActions.ensureUser();
        await PortfolioActions.getPortfolioForEditor();
        store.commit("view/updateViewModeReadiness", true)

        Promise.allSettled([
            UserActions.prefetchPaymentPlanInfo(),
            PortfolioActions.getLatestNotifications(),
            PortfolioActions.getAllAvalibleFonts(),
        ]).catch(error => {
            console.log('Error while loading deferred editor data', error);
        });
    },

    async refreshOnboardingData() {
        store.commit("view/updateViewModeReadiness", false)
        store.state.userDataAndPortfolioDataLoaded = false;
        const templatePreviewsPromise = PortfolioActions.prefetchTemplatePreviews();
        await UserActions.ensureUser();

        if (!store.state.user.defaultCollectionId) {
            store.state.showOnBoardingScreen = true;
            store.state.userPortfolios = [];
        } else {
            await PortfolioActions.getPortfolios();
        }

        await templatePreviewsPromise;
        store.commit("view/updateViewModeReadiness", true)
    },

    setUserDataInSupportWidget(visitorName, visitorEmail) {
        if (window.fwSettings) {
            FreshworksWidget('identify', 'ticketForm', {
                name: visitorName,
                email: visitorEmail
            });
        }
    },
}
